// Landing zones and capacity (#33). Hubs have a few pads, and each field has
// a one-aircraft pad as an alternate landing zone. Your own aircraft keep a
// pad reserved at their base. A hub drone that lands sits on a pad for a
// turnaround. Drones that find their hub full circle in its landing pattern;
// one too many, or one that circles too long, needs an emergency landing at
// a free pad before its countdown runs out, or it comes down in a field.
// Standard shifts only (s.capacityEnabled). Pure, no DOM.

import {
  HUBS, FIELDS, FIELD_PADS, HUB_PADS, PATTERN_CAP, TURNAROUND, EMERGENCY_SECONDS, PATTERN_PATIENCE,
  WRONG_SUPPLY_TURNAROUND, POWER,
  HOVER_PATIENCE, SCORING, ALERT_KINDS,
} from '../config/constants.js';
import { dist } from './geometry.js';
import { hubBlocked, clear as clearAlert } from './alerts.js';
import {
  landAt, landForInspection, isAirborne, tripEnergy,
} from './ops.js';
import { incidentsEndShift } from './limits.js';

export const APPROACH_DIST = 0.09; // where a drone asks for a pad, and circles if there's none

export const ZONES = [
  ...HUBS.map((h, i) => ({ id: `hub-${i}`, label: h.name, short: h.name.replace(' Hub', ''), hubIdx: i, x: h.x, y: h.y, capacity: HUB_PADS })),
  ...FIELD_PADS.map((p) => ({
    id: `pad-${p.fieldIdx}`, label: `${FIELDS[p.fieldIdx].name} pad`, short: FIELDS[p.fieldIdx].name, hubIdx: null,
    fieldIdx: p.fieldIdx, x: p.x, y: p.y, capacity: 1,
  })),
];

export const zoneById = (id) => ZONES.find((z) => z.id === id) ?? null;
export const hubZone = (hubIdx) => ZONES[hubIdx];

/** Pads in use at a zone: your aircraft based there, drones on its pads or cleared to land. */
export function zoneUsed(s, zone) {
  const own = (s.ops?.aircraft ?? []).filter((a) => a.base === zone.id).length;
  const drones = s.drones.filter((d) => d.reserved === zone.id).length;
  return own + drones;
}

export function zoneHasRoom(s, zone) {
  if (zone.hubIdx !== null && hubBlocked(s, zone.hubIdx)) return false;
  return zoneUsed(s, zone) < zone.capacity;
}

/** Drones circling in a zone's landing pattern. */
export const patternCount = (s, zone) => s.drones.filter((d) => d.padWait === zone.id).length;

/** Where a drone is going to land: its divert zone, or its destination hub. */
function landingZone(d) {
  if (d.divertZone) return zoneById(d.divertZone);
  return d.hubEnd === 'to' ? hubZone(d.hubIdx) : null;
}

// ---- emergencies ------------------------------------------------------------

/** Raise an emergency for a drone or one of your aircraft (once). */
export function raiseEmergency(s, craft, reason) {
  if (craft.emergency) return;
  const alert = {
    id: s.alerts.seq++, kind: 'emergency', left: EMERGENCY_SECONDS, fieldIdx: null, hubIdx: null,
    craftId: craft.id, call: craft.call, reason, x: craft.x, y: craft.y,
  };
  craft.emergency = { alertId: alert.id, left: EMERGENCY_SECONDS };
  s.alerts.list.push(alert);
  s.alerts.notes.add('emergency');
}

function endEmergency(s, craft, handled) {
  if (!craft.emergency) return;
  const alert = s.alerts.list.find((a) => a.id === craft.emergency.alertId);
  craft.emergency = null;
  if (!alert) return;
  clearAlert(s, alert, handled);
  if (handled) {
    s.hazardsHandled = (s.hazardsHandled ?? 0) + 1;
    s.rewards.push({ x: alert.x, y: alert.y, points: SCORING.hazardHandled, label: 'emergency landing handled' });
  }
}

/** The free zone nearest a point, skipping `except` (a zone id), or null. */
export function nearestFreeZone(s, at, except = null) {
  return ZONES
    .filter((z) => z.id !== except && zoneHasRoom(s, z))
    .sort((a, b) => dist(at, a) - dist(at, b))[0] ?? null;
}

// ---- landing at a zone (player) ---------------------------------------------

const ownAircraft = (s, id) => s.ops?.aircraft.find((a) => a.id === id) ?? null;

/**
 * What sending a drone or one of your aircraft to land at `zoneId` would do:
 * { ok, label, reason? }. Your aircraft make it their new base.
 */
export function previewLanding(s, craftId, zoneId) {
  const zone = zoneById(zoneId);
  if (!zone || !s.capacityEnabled) return null;
  const a = ownAircraft(s, craftId);
  const label = `Land at ${zone.label}`;
  let supplyNote = null;
  if (a) {
    if (a.base === zone.id) return { ok: false, label, reason: `${a.call} is based there already` };
    // enough battery or fuel to get there (#32)
    const need = tripEnergy(a, [{ x: a.x, y: a.y }, { x: zone.x, y: zone.y }]);
    if (need > a.energy) return { ok: false, label, reason: `Not enough ${POWER[a.power].unit} to get there` };
    const supplied = zone.hubIdx !== null && HUBS[zone.hubIdx].supplies.includes(a.power);
    if (!supplied) supplyNote = `no ${POWER[a.power].name} there: it won't refill`;
  } else {
    const d = s.drones.find((x) => x.id === craftId);
    if (!d) return null;
    if (d.reserved === zone.id || d.divertZone === zone.id) return { ok: false, label, reason: 'Already landing there' };
  }
  if (!zoneHasRoom(s, zone)) {
    return { ok: false, label, reason: zone.hubIdx !== null && hubBlocked(s, zone.hubIdx) ? 'The pad is blocked' : `Full: ${zoneUsed(s, zone)}/${zone.capacity} pads in use` };
  }
  const free = `${zone.capacity - zoneUsed(s, zone)} of ${zone.capacity} free`;
  return { ok: true, label, note: supplyNote ? `${free} · ${supplyNote}` : free };
}

/** Send a drone or one of your aircraft to land at a zone. Returns the preview. */
export function landCraftAt(s, craftId, zoneId) {
  const pv = previewLanding(s, craftId, zoneId);
  if (!pv?.ok) return pv;
  const zone = zoneById(zoneId);
  const a = ownAircraft(s, craftId);
  if (a) {
    landAt(s.ops, a.id, zone);
    endEmergency(s, a, true);
    return pv;
  }
  const d = s.drones.find((x) => x.id === craftId);
  d.divertZone = zone.id;
  d.to = { x: zone.x, y: zone.y };
  d.destLabel = zone.label;
  d.reserved = zone.id;
  if (d.padWait) { d.padWait = null; d.state = 'enroute'; }
  endEmergency(s, d, true);
  return pv;
}

/** The emergency button: land at the nearest free zone. */
export function divertToNearest(s, craftId) {
  const a = ownAircraft(s, craftId);
  const d = a ? null : s.drones.find((x) => x.id === craftId);
  const craft = a ?? d;
  if (!craft) return null;
  const skip = a ? a.base : (d.reserved ?? landingZone(d)?.id);
  const zone = nearestFreeZone(s, craft, skip);
  return zone ? landCraftAt(s, craftId, zone.id) : { ok: false, reason: 'No free pad anywhere' };
}

// ---- stepping ---------------------------------------------------------------

/**
 * Called by sim.step for each airborne drone before it moves: near its
 * landing zone it is cleared to land if there's a pad, or joins the pattern.
 */
export function approachControl(s, d) {
  const zone = landingZone(d);
  if (!zone || d.reserved === zone.id || d.state === 'landed') return;
  if (dist(d, d.to) > APPROACH_DIST) return;
  if (zoneHasRoom(s, zone)) {
    d.reserved = zone.id;
    if (d.padWait) { d.padWait = null; d.state = 'enroute'; }
    return;
  }
  // no pad: circle in the pattern (again, if the player cleared it to go on)
  d.state = 'holding';
  if (!d.padWait) {
    d.padWait = zone.id;
    d.waited = 0;
    if (patternCount(s, zone) > PATTERN_CAP) raiseEmergency(s, d, `${zone.label}'s landing pattern is full`);
  }
}

/** A drone reaches its pad: it lands (and counts) and sits there for its turnaround. */
export function touchDown(s, d) {
  const zone = landingZone(d);
  // a pad without its supply means a long wait for a battery swap or fuel (#32)
  const supplied = zone && zone.hubIdx !== null && (!d.power || HUBS[zone.hubIdx].supplies.includes(d.power));
  if (zone) {
    // park beside the pad, side by side, so landed drones don't stack up
    const n = s.drones.filter((x) => x.state === 'landed' && x.reserved === zone.id).length;
    d.x = zone.x + (n - 0.5) * 0.024;
    d.y = zone.y - (zone.hubIdx === null ? 0.028 : 0.05);
  }
  d.state = 'landed';
  d.reserved = zone ? zone.id : d.reserved;
  d.noSupply = !supplied;
  d.landLeft = TURNAROUND * (supplied ? 1 : WRONG_SUPPLY_TURNAROUND);
  d.trail = [];
  s.cleared += 1;
}

/**
 * A craft comes down in a field (an emergency countdown ran out, or it ran
 * out of battery or fuel): an incident. Yours lands for inspection; a hub
 * drone is lost.
 */
export function forceDown(s, c, label) {
  endEmergency(s, c, false);
  s.incidents += 1;
  s.rewards.push({ x: c.x, y: c.y, points: SCORING.incident, label });
  if (s.ops?.aircraft.includes(c)) landForInspection(s.ops, c.id);
  else s.drones = s.drones.filter((d) => d !== c);
  if (incidentsEndShift(s)) s.running = false;
}

/** Turnarounds, patience in the pattern, and emergency countdowns. */
export function stepZones(s, dt) {
  if (!s.capacityEnabled || !s.running) return;
  // drones on pads take off for elsewhere after their turnaround
  for (const d of s.drones) if (d.state === 'landed') d.landLeft -= dt;
  s.drones = s.drones.filter((d) => d.state !== 'landed' || d.landLeft > 0);

  for (const d of s.drones) {
    if (!d.padWait) continue;
    d.waited += dt;
    if (d.waited > PATTERN_PATIENCE) raiseEmergency(s, d, 'its battery is low after circling');
  }
  for (const a of s.ops?.aircraft ?? []) {
    a.hovered = a.state === 'padHold' ? (a.hovered ?? 0) + dt : 0;
    if (a.hovered > HOVER_PATIENCE) raiseEmergency(s, a, `its pad at ${a.padName} is blocked and its battery is low`);
  }

  // emergency countdowns: land somewhere, or come down in a field
  const craft = [...s.drones, ...(s.ops?.aircraft ?? [])].filter((c) => c.emergency);
  for (const c of craft) {
    c.emergency.left -= dt;
    const alert = s.alerts.list.find((x) => x.id === c.emergency.alertId);
    if (alert) { alert.left = c.emergency.left; alert.x = c.x; alert.y = c.y; }
    if (c.emergency.left > 0) continue;
    // too late: an emergency landing in a field is an incident
    forceDown(s, c, `${c.call} came down in a field`);
  }
  // a craft that landed or left on its own no longer needs its emergency
  for (const a of s.alerts.list.filter((x) => x.kind === 'emergency')) {
    const c = s.drones.find((d) => d.id === a.craftId) ?? s.ops?.aircraft.find((o) => o.id === a.craftId);
    if (!c || !c.emergency || (c.state === 'landed') || (s.ops?.aircraft.includes(c) && !isAirborne(c))) {
      if (c) c.emergency = null;
      clearAlert(s, a, false);
    }
  }
}

/** A short "Barn 2/3 · Shed 1/3 · North Forty 0/1 …" for the panels. */
export function zonesSummary(s) {
  return ZONES.map((z) => {
    const blocked = z.hubIdx !== null && hubBlocked(s, z.hubIdx);
    const pattern = patternCount(s, z);
    return {
      zone: z, used: zoneUsed(s, z), blocked, pattern,
      full: !zoneHasRoom(s, z),
    };
  });
}

export const EMERGENCY_FACT = ALERT_KINDS.emergency.fact;
